import React, { useContext, useState, useEffect } from 'react';
import { AppCtx } from '../AppContext';
import { apiErrorMessage, http } from '../api';
import { DemoNotice } from '../components/States';
import { beginSyntheticDemoSession } from '../demo/runtime/demoSession';

const LogoMark = () => (
  <svg viewBox="0 0 64 64" width="56" height="56" aria-hidden>
    <rect width="64" height="64" rx="18" fill="#0A0A0A" />
    <g fill="none" stroke="white" strokeWidth="3">
      <circle cx="32" cy="32" r="22" />
      <circle cx="32" cy="32" r="10" />
    </g>
    <path d="M32 28v8M28 32h8" stroke="white" strokeWidth="3" strokeLinecap="square" />
  </svg>
);

const DEMO_PERSONAS = [
  {
    id: 'usr-thomas',
    name: 'Thomas Nordstrom',
    title: 'Board Director',
    role: 'super_admin',
    email: 'thomas@talktoplus.io',
    badge: 'Executive Demo',
  },
  {
    id: 'usr-anna-program',
    name: 'Anna Tunnuslause',
    title: 'Program Director',
    role: 'super_admin',
    email: 'anna.program@talktoplus.io',
    badge: 'Program Lead',
  },
  {
    id: 'usr-anna-exec',
    name: 'Anna Virtanen',
    title: 'Executive Sponsor',
    role: 'super_admin',
    email: 'anna.sponsor@talktoplus.io',
    badge: 'Executive',
  },
];


export default function Auth() {
  const { login, t, productMode } = useContext(AppCtx);
  const isDemoFallbackAllowed = productMode === 'DEMO';
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('thomas@talktoplus.io');
  const [password, setPassword] = useState('Thomas!2026');
  const [fullName, setFullName] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  // Check URL query parameters for instant demo login on load (e.g. ?demo=thomas)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const demoParam = params.get('demo') || params.get('autologin') || params.get('user');
    if (demoParam && isDemoFallbackAllowed) {
      const match = DEMO_PERSONAS.find((p) => demoParam.toLowerCase().includes(p.name.split(' ')[0].toLowerCase())) || DEMO_PERSONAS[0];
      handleDirectDemoLogin(match);
    }
  }, [isDemoFallbackAllowed]); // eslint-disable-line

  const handleDirectDemoLogin = (persona) => {
    setBusy(true);
    setErr('');
    beginSyntheticDemoSession();
    const userPayload = {
      id: persona.id,
      email: persona.email,
      full_name: `${persona.name} - ${persona.title}`,
      name: persona.name,
      role: persona.role,
      tenant_id: 'default-tenant',
      title: persona.title,
      demo: true,
    };
    login(`demo-token-${persona.id}`, userPayload);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);

    const emailTrimmed = (email || '').trim().toLowerCase();

    try {
      if (mode === 'login') {
        try {
          const r = await http.post('/auth/login', { email, password });
          if (r?.data?.access_token && r?.data?.user) {
            login(r.data.access_token, r.data.user);
            return;
          }
        } catch (apiErr) {
          if (isDemoFallbackAllowed) {
            // If remote API is unavailable on static hosting (Vercel), resolve demo credentials gracefully
            const matchedPersona = DEMO_PERSONAS.find((p) => p.email.toLowerCase() === emailTrimmed)
              || (emailTrimmed.includes('thomas') ? DEMO_PERSONAS[0] : null)
              || (emailTrimmed.includes('anna') ? DEMO_PERSONAS[1] : null)
              || (emailTrimmed.includes('admin') ? DEMO_PERSONAS[0] : null)
              || DEMO_PERSONAS[0];
            handleDirectDemoLogin(matchedPersona);
            return;
          }
          throw apiErr;
        }
      } else {
        const r = await http.post('/auth/register', { email, password, full_name: fullName });
        login(r.data.access_token, r.data.user);
      }
    } catch (e) {
      setErr(apiErrorMessage(e, mode === 'login' ? 'Login failed. Check the credentials and try again.' : 'Registration failed. Check the fields and try again.'));
    } finally {
      setBusy(false);
    }
  };


  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="auth-page">
      <div className="md:w-1/2 bg-ink text-white p-10 md:p-16 flex flex-col justify-between relative overflow-hidden tkp-grain">
        <div className="relative z-10 flex items-center gap-4">
          <LogoMark />
          <div>
            <div className="font-heading text-2xl font-black tracking-tighter">{t.appName} <span className="text-slate-500">/ {t.appSub}</span></div>
            <div className="text-[11px] text-slate-400 tracking-[0.3em] mt-1">EXECUTION INTELLIGENCE</div>
          </div>
        </div>

        <div className="relative z-10">
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
            From Diagnostic<br />to Prescriptive.
          </h1>
          <p className="mt-6 text-slate-300 text-base max-w-md leading-relaxed">
            Enterprise Execution Intelligence for validated workflow readiness. Demo-safe data stays inside the tenant boundary.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
            {[
              { k: '70%', v: 'of strategies fail at execution' },
              { k: '0', v: 'raw data leaves tenant' },
              { k: '100%', v: 'sovereign' },
            ].map((m, i) => (
              <div key={i}>
                <div className="font-heading text-3xl font-black tracking-tighter">{m.k}</div>
                <div className="text-[10px] tracking-widest uppercase text-slate-400 mt-1">{m.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-[11px] text-slate-500 tracking-wide">
          (c) 2026 TALKTO+. Proprietary. Four-Eyes Principle enforced.
        </div>
      </div>

      <div className="md:w-1/2 flex items-center justify-center p-8 md:p-16 bg-white overflow-y-auto">
        <div className="w-full max-w-sm">
          <div className="flex border-b border-slate-200 mb-6">
            <button
              type="button"
              data-testid="tab-login"
              onClick={() => { setMode('login'); setErr(''); }}
              className={`flex-1 py-3 text-sm font-bold tracking-widest uppercase ${mode === 'login' ? 'border-b-2 border-ink text-ink' : 'text-slate-400'}`}
            >
              {t.auth?.login || 'Sign In'}
            </button>
            <button
              type="button"
              data-testid="tab-register"
              onClick={() => { setMode('register'); setErr(''); }}
              className={`flex-1 py-3 text-sm font-bold tracking-widest uppercase ${mode === 'register' ? 'border-b-2 border-ink text-ink' : 'text-slate-400'}`}
            >
              {t.auth?.register || 'Register'}
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === 'register' && (
              <div>
                <label className="text-[11px] tracking-widest uppercase text-slate-500 font-bold">Full name</label>
                <input data-testid="input-fullname" required value={fullName} onChange={(e) => setFullName(e.target.value)} className="tkp-input mt-2" />
              </div>
            )}
            <div>
              <label className="text-[11px] tracking-widest uppercase text-slate-500 font-bold">{t.auth?.email || 'Email'}</label>
              <input data-testid="input-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="tkp-input mt-2" />
            </div>
            <div>
              <label className="text-[11px] tracking-widest uppercase text-slate-500 font-bold">{t.auth?.password || 'Password'}</label>
              <input data-testid="input-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="tkp-input mt-2" />
            </div>
            {err && <div className="text-xs text-red-700 font-medium border border-red-200 bg-red-50 px-3 py-2 rounded-sm" role="alert">{err}</div>}
            <button type="submit" data-testid="submit-auth" disabled={busy} className="tkp-btn-primary w-full mt-2">
              {busy ? 'Signing in...' : (mode === 'login' ? (t.auth?.signIn || 'Sign In') : (t.auth?.signUp || 'Register'))}
            </button>
          </form>

          {/* Quick Demo Personas 1-Click Access */}
          {productMode === 'DEMO' && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-3 flex items-center justify-between">
                <span>Quick Demo Access</span>
                <span className="text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded">1-Click Live</span>
              </div>
              <div className="space-y-2">
                {DEMO_PERSONAS.map((persona) => (
                  <button
                    key={persona.id}
                    type="button"
                    onClick={() => handleDirectDemoLogin(persona)}
                    className="w-full flex items-center justify-between p-3 text-left border border-slate-200 hover:border-ink hover:bg-slate-50 rounded-lg transition-all group"
                  >
                    <div>
                      <div className="text-xs font-bold text-slate-900 group-hover:text-ink">{persona.name}</div>
                      <div className="text-[11px] text-slate-500">{persona.title}</div>
                    </div>
                    <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded group-hover:bg-ink group-hover:text-white transition-colors">
                      Enter Demo &rarr;
                    </span>
                  </button>
                ))}
              </div>

              <div className="mt-4">
                <DemoNotice>{t.auth?.demoHint || 'Partner demo access is protected. Demo accounts provide instant sovereign executive preview.'}</DemoNotice>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
